import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@components/atoms/link';
import { LinkButton } from '@components/atoms/link-button';
import { Typography } from '@components/atoms/typography';
import { Checkbox } from '@components/moleculs/checkbox';
import { ConsentDisplayModeEnum } from '@models';

import { ConsentBlockContainer } from './styles';
import type { StartConsent } from './use-start-consent';
import { getConsentCheckboxId, getConsentItemLocale } from './use-start-consent';

type ConsentBlockProps = {
    consent: StartConsent;
    signError?: string;
    termsCheckbox?: React.ReactNode;
};

export const ConsentBlock: React.FC<ConsentBlockProps> = ({
    consent,
    signError,
    termsCheckbox,
}) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState<string[]>([]);
    const { data, lang, checkedAt, toggle } = consent;

    if (consent.isError) {
        return (
            <ConsentBlockContainer>
                <Typography tag='div' variant='small-text' color='secondary-a4'>
                    {t('docVerificationWidget.consent.loadError', {
                        defaultValue: 'Could not load the consent. Please try again.',
                    })}
                </Typography>
                <LinkButton dashed={true} onClick={() => consent.refetch()}>
                    {t('docVerificationWidget.consent.retry', { defaultValue: 'Try again' })}
                </LinkButton>
            </ConsentBlockContainer>
        );
    }

    if (!data) {
        return null;
    }

    const retentionUrl = data.policy_urls?.retention?.trim();
    const privacyUrl = data.policy_urls?.privacy?.trim();

    return (
        <ConsentBlockContainer data-testid='docver-widget-consent'>
            <Typography tag='h3' variant='big-text-semibold' color='text-main'>
                {t('docVerificationWidget.consent.title', {
                    defaultValue: 'Identity verification and your consent',
                })}
            </Typography>
            {data.items.map((item) => {
                const text = item.texts[getConsentItemLocale(item, lang)];
                const short = text?.short?.trim();
                const showShort =
                    data.display_mode === ConsentDisplayModeEnum.SHORT &&
                    !!short &&
                    !expanded.includes(item.item_id);

                return (
                    <div key={item.item_id} className='consent-block__item'>
                        <div
                            className={
                                showShort
                                    ? 'consent-block__text'
                                    : 'consent-block__text consent-block__text--full'
                            }
                        >
                            <Typography tag='div' variant='small-text' color='text-main'>
                                {showShort ? short : text?.long}
                            </Typography>
                        </div>
                        {showShort && (
                            <LinkButton
                                dashed={true}
                                onClick={() => setExpanded((prev) => [...prev, item.item_id])}
                            >
                                {t('docVerificationWidget.consent.learnMore', {
                                    defaultValue: 'Learn more',
                                })}
                            </LinkButton>
                        )}
                        {item.checkboxes.map((checkbox) => {
                            const id = getConsentCheckboxId(item, checkbox.key);
                            const label = text?.checkboxes[checkbox.key] ?? '';

                            return (
                                <Checkbox
                                    key={id}
                                    checked={!!checkedAt[id]}
                                    onChange={() => toggle(id)}
                                    label={
                                        <Typography
                                            tag='span'
                                            variant='small-text'
                                            color='text-main'
                                        >
                                            {checkbox.required ? `${label} *` : label}
                                        </Typography>
                                    }
                                />
                            );
                        })}
                    </div>
                );
            })}

            {termsCheckbox}

            {(!!retentionUrl || !!privacyUrl) && (
                <div className='consent-block__links'>
                    {!!retentionUrl && (
                        <Link href={retentionUrl} target='_blank' rel='noreferrer'>
                            {t('docVerificationWidget.consent.retentionPolicy', {
                                defaultValue: 'Retention and destruction policy',
                            })}
                        </Link>
                    )}
                    {!!privacyUrl && (
                        <Link href={privacyUrl} target='_blank' rel='noreferrer'>
                            {t('docVerificationWidget.consent.privacyPolicy', {
                                defaultValue: 'Privacy policy',
                            })}
                        </Link>
                    )}
                </div>
            )}

            {signError && (
                <Typography tag='div' variant='small-text' color='secondary-a4'>
                    {signError}
                </Typography>
            )}
        </ConsentBlockContainer>
    );
};
